import * as yup from "yup";
import dayjs from "dayjs";
import {SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";
import {
    fromGenericBeToGenericFe,
    fromGenericFeToGenericBe,
    fromSingleGenericBeToSingleGenericFe,
    i18nConvertNumberWithDecimals
} from "../../../utils";
import {
    OWAncillaryExceptionBaseDefinitionFormValues,
    OWAncillaryExceptionEntityRequest,
    OWAncillaryExceptionEntityResponse
} from "../../../api/types/owAncillaryException";
import {CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {CommercialSegmentEntityResponse, CommercialSegmentSelectFE} from "../../../api/types/commercialSegment";
import {AncillaryExceptionEntityResponse} from "../../../api/types/ancillaryException";
import {yupPickupFromFunc, yupPickupToFunc, yupReservationFromFunc, yupReservationToFunc} from "../../../yupUtils";

export const initialOWAncillaryExceptionBaseDefinitionValues: OWAncillaryExceptionBaseDefinitionFormValues = {
    prcOWAncillary: null,
    discountIncreaseSelected: "discountIncreasePercentage",
    discountIncreasePercentage: null,
    discountIncreaseFixed: null,
    name: "",
    commercialCategory: null,
    note: null,
    reservationFrom: "",
    reservationTo: "",
    pickupFrom: "",
    pickupTo: "",
    tpUnit: "",
    currency: "EUR",
    amountMin: null,
    amountMax: null,
    chargingWay: "",
    chargeType: "",
    maxUnits: null,
    commercialSegment: null,
    code: "",
    nameAncillary: "",
    description: "",
    type: "",
    subtype: "",
    cardinality: null,
    sellDesk: false,
    applicationTiming: null,
    toleranceTime: null,
    additionalField: null,
    tax: false,
    taxCode: "",
    dealer: [],
    legalEntity: []

};

export const getOWAncillaryExceptionBaseDefinitionValidationSchema = (t: Function, ancillaryException: AncillaryExceptionEntityResponse | null) => {
    const yupReservationFrom = yupReservationFromFunc({
        t,
        id: ancillaryException?.id || null,
        reservationFrom: ancillaryException?.reservationFrom as string,
    });
    const yupReservationTo = yupReservationToFunc({
        t,
        id: ancillaryException?.id || null,
        reservationTo: ancillaryException?.reservationTo as string,
        reservationFromFieldName: "reservationFrom"
    });
    const yupPickupFrom = yupPickupFromFunc({
        t,
        id: ancillaryException?.id || null,
        pickupFrom: ancillaryException?.pickupFrom as string,
    });
    const yupPickupTo = yupPickupToFunc({
        t,
        id: ancillaryException?.id || null,
        reservationTo: ancillaryException?.reservationTo as string,
        pickupFrom: ancillaryException?.pickupFrom as string,
        pickupTo: ancillaryException?.pickupTo as string,
        pickupFromFieldName: "pickupFrom"
    });

    return yup.object().shape({
        reservationFrom: yupReservationFrom,
        reservationTo: yupReservationTo,
        pickupFrom: yupPickupFrom,
        pickupTo: yupPickupTo,
        unit: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        dealer: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string(),
            })
            .required(t("translation:validation.fieldMandatory"))
            .nullable(true),
        legalEntity: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string(),
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),

        commercialCategory: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        commercialSegment: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        prcOWAncillary: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        currency: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        amountMin: yup.number()
            .nullable()
            .min(0, t("pricing:rentalPeriods.form.errors.positiveNumber")),
        amountMax: yup.number()
            .nullable()
            .min(yup.ref("amountMin"), t("pricing:ancillary.form.errors.amountMax")),
        maxUnits: yup.number().nullable().min(0, t("pricing:rentalPeriods.form.errors.positiveNumber")),
        name: yup.string().nullable().required(t("translation:validation.fieldMandatory")),
        prcAncillary: yup.object()
            .shape({
                code: yup.string(),
                label: yup.string()
            }).nullable().required(t("translation:validation.fieldMandatory")),
        chargeType: yup.string().nullable().required(t("translation:validation.fieldMandatory"))
    });
};

export const buildEntityRequestFromFormValues = (watchAllFields: OWAncillaryExceptionBaseDefinitionFormValues, owAncillaryExceptionInUse: boolean): OWAncillaryExceptionEntityRequest => {
    return {
        amountMin: watchAllFields.amountMin as number > 0 ? Number(i18nConvertNumberWithDecimals(Number(watchAllFields.amountMin) * 100)) : null,
        amountMax: watchAllFields.amountMax as number > 0 ? Number(i18nConvertNumberWithDecimals(Number(watchAllFields.amountMax) * 100)) : null,
        chargeType: watchAllFields.chargeType,
        chargingWay: watchAllFields.chargingWay || "",
        code: String(watchAllFields.prcOWAncillary?.ancillaryCode),
        currency: watchAllFields.prcOWAncillary?.currency || "",
        description: watchAllFields.description || "",
        note: watchAllFields.note ? watchAllFields.note as string : null,
        pickupFrom: dayjs(watchAllFields.pickupFrom).toISOString(),
        pickupTo: dayjs(watchAllFields.pickupTo).toISOString(),
        reservationFrom: dayjs(watchAllFields.reservationFrom).toISOString(),
        reservationTo: dayjs(watchAllFields.reservationTo).toISOString(),
        timeUnitsMax: watchAllFields.maxUnits === 0 ? null : watchAllFields.maxUnits as number,
        type: watchAllFields.prcOWAncillary?.type || "",
        subtype: watchAllFields.prcOWAncillary?.subtype || "",
        name: watchAllFields.name,
        tpUnit: watchAllFields.prcOWAncillary?.tpUnit as string,
        commercialCategory: {
            id: watchAllFields?.commercialCategory?.code || "",
            name: watchAllFields?.commercialCategory?.label || "",
            commercialSegment: watchAllFields?.commercialCategory?.commercialSegment as CommercialSegmentEntityResponse,
            active: watchAllFields?.commercialCategory?.active as boolean,
        },
        cardinality: watchAllFields?.cardinality as number,
        prcAncillary: {
            ...watchAllFields.prcOWAncillary,
            id: watchAllFields.prcOWAncillary?.code as number,
            code: watchAllFields.prcOWAncillary?.ancillaryCode as string,
            reservationFrom: watchAllFields.prcOWAncillary?.reservationFrom as string,
            reservationTo: watchAllFields.prcOWAncillary?.reservationTo as string,
            pickupFrom: watchAllFields.prcOWAncillary?.pickupFrom as string,
            pickupTo: watchAllFields.prcOWAncillary?.pickupTo as string,
            tpUnit: watchAllFields.prcOWAncillary?.tpUnit,
            currency: watchAllFields.prcOWAncillary?.currency as string,
            amountMin: watchAllFields.prcOWAncillary?.amountMin ? Number(i18nConvertNumberWithDecimals(Number(watchAllFields.amountMin) * 100)) : null,
            amountMax: watchAllFields.prcOWAncillary?.amountMax ? Number(i18nConvertNumberWithDecimals(Number(watchAllFields.amountMax) * 100)) : null,
            dealerId: watchAllFields.prcOWAncillary?.dealerId as number,
            chargingWay: watchAllFields.prcOWAncillary?.chargingWay as string,
            maxUnits: watchAllFields.prcOWAncillary?.maxUnits === 0 ? null : watchAllFields.prcOWAncillary?.maxUnits as number,
            chargeType: watchAllFields.prcOWAncillary?.chargeType as string,
            nameAncillary: watchAllFields.prcOWAncillary?.nameAncillary as string,
            description: watchAllFields.prcOWAncillary?.description as string,
            type: watchAllFields.prcOWAncillary?.type as string,
            subtype: watchAllFields.prcOWAncillary?.subtype as string,
            segments: fromGenericFeToGenericBe(watchAllFields.prcOWAncillary?.segments as SelectElementModelFE[]),
            cardinality: watchAllFields.prcOWAncillary?.cardinality as number,
            sellDesk: watchAllFields.prcOWAncillary?.sellDesk as boolean,
            passageToCounterNeeded: watchAllFields.prcOWAncillary?.passageToCounterNeeded as boolean,
            applicationTiming: watchAllFields.prcOWAncillary?.applicationTiming === null ? null : watchAllFields.prcOWAncillary?.applicationTiming === "PICK UP" ? "PICK_UP" : "DROP_OFF",
            toleranceTime: watchAllFields.prcOWAncillary?.toleranceTime as number,
            flagResellableAtDeskPostRa: watchAllFields.prcOWAncillary?.flagResellableAtDeskPostRa ?? false,
            flagResellableInSubstitution: watchAllFields.prcOWAncillary?.flagResellableInSubstitution ?? false,
            flagDiscountApplicable: watchAllFields.prcOWAncillary?.flagDiscountApplicable ?? false,
            timeToChange: watchAllFields.prcOWAncillary?.timeToChange ?? null,
            timeToRemove: watchAllFields.prcOWAncillary?.timeToRemove ?? null,
            additionalField: watchAllFields.prcOWAncillary?.additionalField as string,
            tax: watchAllFields.prcOWAncillary?.tax as boolean,
            taxCode: watchAllFields.prcOWAncillary?.taxCode as string,
            isOneWay: watchAllFields.prcOWAncillary?.isOneWay as boolean,
            used: owAncillaryExceptionInUse as boolean,
            dealer: (watchAllFields.prcOWAncillary?.dealer as SelectElementModelFE).code as string,
            legalEntity: (watchAllFields.prcOWAncillary?.legalEntity as SelectElementModelFE).code as string,
        },
        commercialSegment: {
            id: watchAllFields?.commercialSegment?.code as string,
            name: watchAllFields?.commercialSegment?.label as string,
            active: watchAllFields?.commercialSegment?.active as boolean,
        },
        relatedDiscountIncreasePercentage: watchAllFields.discountIncreasePercentage || "",
        relatedDiscountIncreaseFixed: Number(watchAllFields.discountIncreaseFixed?.toString().replace(",", ".")) * 100 || "",
        sellDesk: watchAllFields?.prcOWAncillary?.sellDesk as boolean,
        passageToCounterNeeded: watchAllFields?.prcOWAncillary?.passageToCounterNeeded as boolean,
        applicationTiming: watchAllFields?.applicationTiming === null ? null : watchAllFields?.applicationTiming === "PICK UP" ? "PICK_UP" : "DROP_OFF",
        toleranceTime: watchAllFields?.prcOWAncillary?.toleranceTime as number,
        additionalField: watchAllFields.prcOWAncillary?.additionalField || null,
        tax: watchAllFields.prcOWAncillary?.tax as boolean,
        taxCode: watchAllFields.prcOWAncillary?.taxCode as string,
        used: owAncillaryExceptionInUse as boolean,
        dealer: (watchAllFields.dealer as SelectElementModelFE).code as string,
        legalEntity: (watchAllFields.legalEntity as SelectElementModelFE).code as string,
    };
};

export const buildOWAncillaryExceptionBaseDefinitionValues = (owAncillaryException: OWAncillaryExceptionEntityResponse,
                                                              services: any, t?: any): OWAncillaryExceptionBaseDefinitionFormValues => {
    return {
        id: owAncillaryException.id,
        prcOWAncillary: {
            ...owAncillaryException.prcAncillary,
            code: String(owAncillaryException.prcAncillary?.id),
            ancillaryCode: String(owAncillaryException.prcAncillary?.code),
            label: owAncillaryException.prcAncillary?.nameAncillary as string,
            reservationFrom: owAncillaryException.prcAncillary?.reservationFrom as string,
            reservationTo: owAncillaryException.prcAncillary?.reservationTo as string,
            pickupFrom: owAncillaryException.prcAncillary?.pickupFrom as string,
            pickupTo: owAncillaryException.prcAncillary?.pickupTo as string,
            tpUnit: owAncillaryException.prcAncillary?.tpUnit,
            currency: owAncillaryException.prcAncillary?.currency as string,
            amountMin: owAncillaryException.prcAncillary?.amountMin ? owAncillaryException.prcAncillary?.amountMin as number : null,
            amountMax: owAncillaryException.prcAncillary?.amountMax ? owAncillaryException.prcAncillary?.amountMax as number : null,
            dealerId: owAncillaryException.prcAncillary?.dealerId as number,
            chargingWay: owAncillaryException.prcAncillary?.chargingWay as string,
            maxUnits: owAncillaryException.prcAncillary?.maxUnits as number,
            chargeType: owAncillaryException.prcAncillary?.chargeType as string,
            nameAncillary: owAncillaryException.prcAncillary?.nameAncillary as string,
            description: owAncillaryException.prcAncillary?.description as string,
            type: owAncillaryException.prcAncillary?.type as string,
            subtype: owAncillaryException.prcAncillary?.subtype as string,
            segments: fromGenericBeToGenericFe(owAncillaryException.prcAncillary?.segments as SelectElementModelBE[]),
            cardinality: owAncillaryException.prcAncillary?.cardinality as number,
            sellDesk: owAncillaryException.prcAncillary?.sellDesk as boolean,
            passageToCounterNeeded: owAncillaryException.prcAncillary?.passageToCounterNeeded as boolean,
            applicationTiming: owAncillaryException.prcAncillary?.applicationTiming === null ? null : owAncillaryException.prcAncillary?.applicationTiming === "PICK_UP" ? "PICK UP" : "DROP OFF",
            toleranceTime: owAncillaryException.prcAncillary?.toleranceTime as number,
            flagResellableAtDeskPostRa: owAncillaryException.prcAncillary?.flagResellableAtDeskPostRa ?? false,
            flagResellableInSubstitution: owAncillaryException.prcAncillary?.flagResellableInSubstitution ?? false,
            flagDiscountApplicable: owAncillaryException.prcAncillary?.flagDiscountApplicable ?? false,
            timeToChange: owAncillaryException.prcAncillary?.timeToChange ?? null,
            timeToRemove: owAncillaryException.prcAncillary?.timeToRemove ?? null,
            additionalField: owAncillaryException.prcAncillary?.additionalField as string,
            tax: owAncillaryException.prcAncillary?.tax as boolean,
            taxCode: services.find((s: any) => s.code === owAncillaryException.prcAncillary?.taxCode as string)?.name || null,
            isOneWay: owAncillaryException.prcAncillary?.isOneWay as boolean,
            dealer: {
                code: owAncillaryException.prcAncillary?.dealer as string,
                label: t('translation:dealers.'.concat(owAncillaryException.prcAncillary?.dealer as string))
            },
            legalEntity: {
                code: owAncillaryException.prcAncillary?.legalEntity as string,
                label: t('translation:legalEntities.'.concat(owAncillaryException.prcAncillary?.legalEntity as string))
            },
        },

        discountIncreaseSelected: "discountIncreasePercentage",
        discountIncreasePercentage: owAncillaryException.relatedDiscountIncreasePercentage,
        discountIncreaseFixed: i18nConvertNumberWithDecimals(Number(owAncillaryException.relatedDiscountIncreaseFixed) / 100),
        name: owAncillaryException.name,


// FIELDS FROM ANCILLARY
        commercialCategory: fromSingleGenericBeToSingleGenericFe(owAncillaryException.commercialCategory) as CommercialCategorySelectFE || null,
        note: owAncillaryException.note || null,
        reservationFrom: owAncillaryException.reservationFrom,
        reservationTo: owAncillaryException.reservationTo,
        pickupFrom: owAncillaryException.pickupFrom,
        pickupTo: owAncillaryException.pickupTo,
        tpUnit: owAncillaryException.tpUnit,
        currency: owAncillaryException.currency,
        amountMin: owAncillaryException.amountMin ? Number(i18nConvertNumberWithDecimals(Number(owAncillaryException.amountMin) / 100)) : null,
        amountMax: owAncillaryException.amountMax ? Number(i18nConvertNumberWithDecimals(owAncillaryException.amountMax / 100)) : null,
        chargingWay: owAncillaryException.chargingWay,
        chargeType: owAncillaryException.chargeType,
        maxUnits: owAncillaryException.timeUnitsMax,
        commercialSegment: fromSingleGenericBeToSingleGenericFe(owAncillaryException.commercialSegment) as CommercialSegmentSelectFE,
        code: owAncillaryException.code,
        nameAncillary: owAncillaryException.prcAncillary?.nameAncillary as string,
        description: owAncillaryException.description,
        type: owAncillaryException.type,
        subtype: owAncillaryException.subtype,
        cardinality: owAncillaryException.cardinality,
        sellDesk: owAncillaryException.sellDesk,
        applicationTiming: owAncillaryException?.applicationTiming === null ? null : owAncillaryException?.applicationTiming === "PICK_UP" ? "PICK UP" : "DROP OFF",
        toleranceTime: owAncillaryException.toleranceTime,
        additionalField: owAncillaryException.additionalField,
        tax: owAncillaryException.tax,
        taxCode: services.find((s: any) => s.code === owAncillaryException.taxCode as string)?.name || null,
        dealer: {
            code: owAncillaryException.dealer as string,
            label: t('translation:dealers.'.concat(owAncillaryException.dealer as string))
        },
        legalEntity: {
            code: owAncillaryException.legalEntity as string,
            label: t('translation:legalEntities.'.concat(owAncillaryException.legalEntity as string))
        },
    };
};